/**
 * The one place a date, a time or a duration becomes text.
 *
 * Replaces five helpers that disagreed with each other:
 *
 *   formatDate       19/08/2026 - 12:09   UTC
 *   dateToString     8/19/2026 12:09      browser-local, and locale-dependent
 *   timezoneToDate   2026/08/19 12:09     neither — it sliced the ISO string
 *   moment(x).format 2026.08.19 12:08     browser-local, and MM in the minute slot
 *   a sixth, local to reservation/detail/lib/info, date-only and browser-local
 *
 * TIMEZONE POLICY — browser-local, everywhere, deliberately.
 *
 * The API sends ISO-8601 with an offset, so the instant is unambiguous; the
 * only question is which clock to show it on. Half the panel already showed
 * browser-local and half showed UTC, which meant two screens could disagree
 * about the day for a record created near midnight. Local wins because these
 * are operational timestamps read by people sitting in the office the events
 * happened in — "when did this ticket open" means their clock, not UTC's.
 *
 * Nothing here converts. If a screen ever needs a fixed office timezone, that
 * is a change to this file and to nothing else.
 *
 * FORMAT — one shape, `YYYY.MM.DD HH:mm`, drawn in --font-mono with tabular
 * numerals so columns of them line up. The design file draws dates two ways
 * (`2025.09.01 12:09` on the ticket detail, `18/06/2025` on the reservation
 * detail); this follows the first, because the brief asks for one format and
 * the mono/tabular treatment is specified against that one.
 */

/** Rendered when a value is absent. Never a bare "-". */
export const MISSING = {
  /** No one has been given this yet — an owner, an assignee, a number. */
  assigned: 'Not assigned',
  /** Nobody filled this in — a subject, a description, a note. */
  provided: 'Not provided',
  /** Not applicable, or nothing worth saying. An em dash. */
  none: '—'
} as const;

/** Parses anything the API might send. Returns null rather than Invalid Date. */
const toDate = (value?: string | number | Date | null): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * `2026.08.19 14:32` — the default for every timestamp in the product.
 * Browser-local. Returns '' for a missing or unparseable value, so a call
 * site can fall back with `||`.
 */
export const formatDateTime = (value?: string | number | Date | null): string => {
  const d = toDate(value);
  if (!d) return '';
  return `${formatDay(d)} ${formatTime(d)}`;
};

/** `2026.08.19` — for a date with no meaningful time of day. Browser-local. */
export const formatDay = (value?: string | number | Date | null): string => {
  const d = toDate(value);
  if (!d) return '';
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;
};

/** `14:32` — 24-hour, browser-local. */
export const formatTime = (value?: string | number | Date | null): string => {
  const d = toDate(value);
  if (!d) return '';
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * `2025.06.18` from the three fields the reservation endpoints actually send.
 *
 * Reservations are the one thing in the product with no timestamp on the
 * wire: both the list and the detail send `day` (18), `month` ("June") and
 * `year` (2025) as separate fields. The detail *also* sends `startDate` as a
 * UTC instant — and it must not be used, because the times beside it
 * (`start_time`, `end_time`) are office wall-clock. Rendering the date from
 * one and the times from the other makes a screen that disagrees with itself
 * for any viewer outside the office timezone.
 *
 * So a reservation is drawn entirely from its own local fields. This is the
 * one place the timezone policy at the top of this file does not apply, and
 * deliberately: a room booking is an appointment on a wall clock, not an
 * instant. "16:00 in the meeting room" is 16:00 to everyone who reads it.
 */
export const formatReservationDay = (
  day?: string | number | null,
  month?: string | number | null,
  year?: string | number | null
): string => {
  if (day === null || day === undefined || !month || !year) return '';
  return formatDay(new Date(`${month} ${day}, ${year}`));
};

/**
 * `09:54` — a wall-clock string from the API, padded so a column of them
 * lines up. The reservation endpoints send `9:54` and `16:00` in the same
 * payload; in tabular numerals the unpadded one sits a digit out of line.
 */
export const formatClock = (value?: string | null): string => {
  if (!value) return '';
  const [h, m] = String(value).trim().split(':');
  if (m === undefined) return String(value);
  return `${pad(Number(h))}:${m}`;
};

/**
 * A duration in milliseconds as minutes: `0 min`, `7.5 min`, `142 min`.
 *
 * Replaces `convertMsToMin`, which had two bugs that compounded:
 *
 *   1. It truncated decimals with `minutes.toString().substring(0, 4)`, so
 *      100.9 minutes became "100." and 7.06 became "7.06" — the precision it
 *      kept depended on how many digits were to the left of the point.
 *   2. It returned the NUMBER 0 for a zero duration, and every one of its
 *      eleven call sites was written `convertMsToMin(x, 'Min') || 'N/A'`.
 *      0 is falsy, so a genuinely zero-minute delivery displayed as "N/A" —
 *      the fastest possible service, reported as missing data.
 *
 * So this rounds properly, always returns a string, and distinguishes "zero"
 * from "no value": zero renders `0 min`, absent renders the em dash. Call
 * sites must not add a `|| 'N/A'` guard — that is what re-introduces bug 2.
 */
export const formatDuration = (ms?: number | string | null): string => {
  if (ms === null || ms === undefined || ms === '') return MISSING.none;
  const value = typeof ms === 'string' ? Number(ms) : ms;
  if (!Number.isFinite(value)) return MISSING.none;

  const minutes = value / 1000 / 60;
  // One decimal under ten minutes, whole minutes above — a 143.7-minute
  // average does not earn a decimal, a 2.4-minute one does.
  const rounded = minutes < 10 ? Math.round(minutes * 10) / 10 : Math.round(minutes);
  return `${rounded} min`;
};
